<template>
  <div id="ascAuditReport" v-loading="loading" element-loading-spinner="atom-audit-loader">
    <div v-if="!loading">
      <div class="options print-hidden report-header-row">
        <el-row>
          <el-col :span="22">
            <el-row>
              <el-col :span="6">
                <el-button type="text" @click="selectAll">Select All</el-button>
                <el-button type="text" @click="unselectAll">Unselect All</el-button>
              </el-col>
            </el-row>

            <el-row class="hidden-sm">
              <el-col :span="6" v-for="(chunk, i) in chunkDashboards(4)" :key="i">
                <div v-for="dashboard in chunk" :key="dashboard.name">
                  <input type="checkbox" :checked="getShowFlag(dashboard.showFlag)" @change="toggleShowFlag(dashboard.showFlag)" />
                  <label class="toc"
                    ><a href="#" @click.prevent="scrollTo(dashboard.scrollTo)">{{ dashboard.name }}</a></label
                  >
                </div>
              </el-col>
            </el-row>

            <el-row class="hidden-lg">
              <el-col :span="8" v-for="(chunk, i) in chunkDashboards(3)" :key="i">
                <div v-for="dashboard in chunk" :key="dashboard.name">
                  <input type="checkbox" :checked="getShowFlag(dashboard.showFlag)" @change="toggleShowFlag(dashboard.showFlag)" />
                  <label class="toc"
                    ><a href="#" @click.prevent="scrollTo(dashboard.scrollTo)">{{ dashboard.name }}</a></label
                  >
                </div>
              </el-col>
            </el-row>
          </el-col>
          <el-col :span="2">
            <el-button icon="mdi mdi-file-download-outline" class="pull-right print-hidden hiaButton" round plain style="margin-top: 30px; margin-bottom: 0px; z-index: 100;" size="mini" @click="saveReport">Download </el-button>
          </el-col>
        </el-row>

        <el-row v-if="showWorksheets">
          <div style="display: inline-block; padding: 15px 10px 15px 10px; background-color: #f3f2f1a9; margin-top: 15px; margin-bottom: 10px; border-radius: 5px; ">
            <h5 style="margin-bottom: 8px; margin-top: 0px;">Optional Parameters</h5>
            <input type="checkbox" v-model="optionalParameters.hideWorksheetCoderResponses" />
            <label class="toc">Hide Worksheet Coder Responses</label>
          </div>
        </el-row>
      </div>
      <div class="mainReport" style="margin-top: 20px;">
        <TenantReportHeader></TenantReportHeader>
        <h1 contenteditable style="margin-top: 20px;" @input="setTitle($event)">
          {{
            title
              ? `Ambulatory Coding Audit
          Results for ${title}`
              : `Ambulatory Coding Audit Results`
          }}<i style="font-size: 16px; margin-left: 5px; cursor: pointer; color: #b4bccc" class="mdi mdi-pencil print-hidden"></i>
        </h1>
        <h4 v-if="codeTypesDesc !== ''">{{ codeTypesDesc }} Code Types</h4>

        <div v-for="(dashboard, i) in dashboards.filter(f => getShowFlag(f.showFlag) && shouldShowDashboardOption(f))" :key="dashboard.name">
          <div>
            <h2 :class="i > 0 ? 'page-break' : ''" :id="dashboard.scrollTo">{{ dashboard.name }}</h2>

            <template v-if="dashboard.component !== 'IPWorksheetsPrintable'">
              <component ref="parentComponent" :is="dashboard.component" :ascTableIds="ascTableIds" :codeTypes="codeTypes" :optionalParameters="optionalParameters"> </component>
            </template>
          </div>
        </div>
        <span class="watermark">Powered by Atom Audit</span>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapState } from 'vuex'
  import TenantReportHeader from '@/views/reports/printable/TenantReportHeader.vue'
  import ASCBasicSummaryPrintable from './printable/ASC_BasicSummaryPrintable.vue'
  import ASCByCoderPrintable from './printable/ASC_ByCoderPrintable.vue'
  import ASCByOriginalAuditorPrintable from './printable/ASC_ByOriginalAuditorPrintable.vue'
  import ASCByPatientTypePrintable from './printable/ASC_ByPatientTypePrintable.vue'
  import ASCByFacilityPrintable from './printable/ASC_ByFacilityPrintable.vue'
  import ASCByProjectPrintable from './printable/ASC_ByProjectPrintable.vue'
  import ASCByServiceLinePrintable from './printable/ASC_ByServiceLinePrintable.vue'
  import ASCByDxPrintable from './printable/ASC_ByDxPrintable.vue'
  import ASCByHccPrintable from './printable/ASC_ByHCCPrintable.vue'
  import ASCByHccV28Printable from './printable/ASC_ByHCCV28Printable.vue'
  import ASCByCptWithReimbPrintable from './printable/ASC_ByCptWithReimbPrintable.vue'
  import ASCByCptWithoutReimbPrintable from './printable/ASC_ByCptWithoutReimbPrintable.vue'
  import ASCByModifierPrintable from './printable/ASC_ByModifierPrintable.vue'
  import ASCByReasonPrintable from './printable/ASC_ByReasonPrintable.vue'
  import OPWorksheetsPrintable from './printable/OP_WorksheetsPrintable.vue'
  import ASCTrending from './printable/ASC_Trending.vue'
  import ASCChecklistSummaryPrintable from './printable/ASC_ChecklistSummaryPrintable.vue'
  import ASCActivityAuditResultsPrintable from './printable/ASC_ActivityAuditResultsPrintable.vue'
  import ASCActivityAuditByCoderPrintable from './printable/ASC_ActivityAuditByCoderPrintable.vue'

  import ASCCombinedFindingsPrintable from './printable/ASC_FindingSummaryCombinedPrintable.vue'
  import ASCDxFindingsPrintable from './printable/ASC_FindingSummaryDXPrintable.vue'
  import ASCCptFindingsPrintable from './printable/ASC_FindingSummaryCPTPrintable.vue'
  import ASCWorksheetFindingsPrintable from './printable/ASC_FindingSummaryWorksheetPrintable.vue'
  import ASCModFindingsPrintable from './printable/ASC_FindingSummaryModPrintable.vue'
  import ASCRebuttalsByAuditorPrintable from './printable/ASC_RebuttalsByAuditorPrintable.vue'
  import ASCRebuttalsByCoderPrintable from './printable/ASC_RebuttalsByCoderPrintable.vue'

  export default {
    name: 'AmbulatoryAuditReport',
    components: {
      TenantReportHeader,
      ASCBasicSummaryPrintable,
      ASCByCoderPrintable,
      ASCByOriginalAuditorPrintable,
      ASCByPatientTypePrintable,
      ASCByFacilityPrintable,
      ASCByProjectPrintable,
      ASCByServiceLinePrintable,
      ASCByDxPrintable,
      ASCByHccPrintable,
      ASCByHccV28Printable,
      ASCByCptWithReimbPrintable,
      ASCByCptWithoutReimbPrintable,
      ASCByModifierPrintable,
      ASCByReasonPrintable,
      OPWorksheetsPrintable,
      ASCTrending,
      ASCCombinedFindingsPrintable,
      ASCDxFindingsPrintable,
      ASCCptFindingsPrintable,
      ASCWorksheetFindingsPrintable,
      ASCModFindingsPrintable,
      ASCChecklistSummaryPrintable,
      ASCActivityAuditResultsPrintable,
      ASCActivityAuditByCoderPrintable,
      ASCRebuttalsByAuditorPrintable,
      ASCRebuttalsByCoderPrintable
    },
    props: [],
    data: function() {
      return {
        title: null,
        loading: false,
        ascTableIds: null,
        codeTypes: [],
        codeTypesDesc: '',
        optionalParameters: {
          hideWorksheetCoderResponses: false
        },
        showByCoder: true,
        showByProvider: true,
        showByPatientType: true,
        showByFacility: true,
        showByReason: true,
        showByProject: true,
        showByServiceLine: true,
        showDx: true,
        showHcc: true,
        showHccV28: true,
        showEm: true,
        showByCptWithReimb: true,
        showByCptWithoutReimb: true,
        showMod: true,
        showEmLevel: true,
        showWorksheets: false,
        showPx: true,
        showAccSumm: true,
        showTrending: true,
        showWorksheetFindings: true,
        showCombinedFindings: true,
        showDxFindings: true,
        showCptFindings: true,
        showModFindings: true,
        showChecklists: true,
        showActivityAuditResults: true,
        showActivityAuditByCoder: true,
        showRebuttalsByAuditor: true,
        showRebuttalsByCoder: true,
        showLevel2Audit: false,
        showByOriginalAuditor: true,
        dashboards: [
          { name: 'Accuracy Summary', component: 'ASCBasicSummaryPrintable', showFlag: 'showAccSumm', scrollTo: 'accSumm', reportSectionOnly: false },
          { name: 'Worksheet Findings Summary', component: 'ASCWorksheetFindingsPrintable', showFlag: 'showWorksheetFindings', scrollTo: 'auditFindings', reportSectionOnly: false },
          { name: 'Combined Code Change Summary', component: 'ASCCombinedFindingsPrintable', showFlag: 'showCombinedFindings', scrollTo: 'combinedFindings', reportSectionOnly: false },
          { name: 'Dx Change Summary', component: 'ASCDxFindingsPrintable', showFlag: 'showDxFindings', scrollTo: 'dxFindings', reportSectionOnly: false },
          { name: 'CPT Change Summary', component: 'ASCCptFindingsPrintable', showFlag: 'showCptFindings', scrollTo: 'cptFindings', reportSectionOnly: false },
          { name: 'Modifier Change Summary', component: 'ASCModFindingsPrintable', showFlag: 'showModFindings', scrollTo: 'modFindings', reportSectionOnly: false },
          { name: 'By Coder', component: 'ASCByCoderPrintable', showFlag: 'showByCoder', scrollTo: 'byCoder', reportSectionOnly: false },
          { name: 'By Original Auditor', component: 'ASCByOriginalAuditorPrintable', showFlag: 'showByOriginalAuditor', scrollTo: 'byOriginalAuditor', reportSectionOnly: false },
          { name: 'ASC Trending', component: 'ASCTrending', showFlag: 'showTrending', scrollTo: 'ipTrending', reportSectionOnly: true },
          { name: 'By Patient Type', component: 'ASCByPatientTypePrintable', showFlag: 'showByPatientType', scrollTo: 'byPatientType', reportSectionOnly: false },
          { name: 'By Facility', component: 'ASCByFacilityPrintable', showFlag: 'showByFacility', scrollTo: 'byFacility', reportSectionOnly: false },
          { name: 'By Project', component: 'ASCByProjectPrintable', showFlag: 'showByProject', scrollTo: 'byProject', reportSectionOnly: false },
          { name: 'By Service Line', component: 'ASCByServiceLinePrintable', showFlag: 'showByServiceLine', scrollTo: 'byServiceLine', reportSectionOnly: false },
          { name: 'By ASC Reason', component: 'ASCByReasonPrintable', showFlag: 'showByReason', scrollTo: 'byServiceLine', reportSectionOnly: false },
          { name: 'Dx Summary', component: 'ASCByDxPrintable', showFlag: 'showDx', scrollTo: 'byDx', reportSectionOnly: false },
          { name: 'HCC V24 Summary', component: 'ASCByHccPrintable', showFlag: 'showHcc', scrollTo: 'byHCC', reportSectionOnly: false },
          { name: 'HCC V28 Summary', component: 'ASCByHccV28Printable', showFlag: 'showHccV28', scrollTo: 'byHCCV28', reportSectionOnly: false },
          { name: 'CPTs with Reimb.', component: 'ASCByCptWithReimbPrintable', showFlag: 'showByCptWithReimb', scrollTo: 'byCpt', reportSectionOnly: false },
          { name: 'CPTs without Reimb.', component: 'ASCByCptWithoutReimbPrintable', showFlag: 'showByCptWithoutReimb', scrollTo: 'byCpt', reportSectionOnly: false },
          { name: 'Modifier Summary', component: 'ASCByModifierPrintable', showFlag: 'showMod', scrollTo: 'byPx', reportSectionOnly: false },
          { name: 'Checklists', component: 'ASCChecklistSummaryPrintable', showFlag: 'showChecklists', scrollTo: 'checklists', reportSectionOnly: false },
          { name: 'Activity Audit Results', component: 'ASCActivityAuditResultsPrintable', showFlag: 'showActivityAuditResults', scrollTo: 'activityAuditResults', reportSectionOnly: false, featureFlag: 'activityAuditsEnabled' },
          { name: 'Activity Audits by Coder', component: 'ASCActivityAuditByCoderPrintable', showFlag: 'showActivityAuditByCoder', scrollTo: 'activityAuditByCoder', reportSectionOnly: false, featureFlag: 'activityAuditsEnabled' },
          { name: 'Rebuttals by Auditor', component: 'ASCRebuttalsByAuditorPrintable', showFlag: 'showRebuttalsByAuditor', scrollTo: 'rebuttalsByAuditor', reportSectionOnly: false },
          { name: 'Rebuttals by Coder', component: 'ASCRebuttalsByCoderPrintable', showFlag: 'showRebuttalsByCoder', scrollTo: 'rebuttalsByCoder', reportSectionOnly: false },
          { name: 'Worksheets', component: 'OPWorksheetsPrintable', showFlag: 'showWorksheets', scrollTo: 'drgChangeWorksheets', reportSectionOnly: false }
        ]
      }
    },
    methods: {
      ...mapActions('managedLists/tenantSetup/', ['GET_TENANT_SETUP']),
      setTitle(evt) {
        window.document.title = evt.target.innerText
      },
      saveReport() {
        window.print()
      },
      scrollTo(id) {
        const target = document.getElementById(id)
        if (target) {
          window.scroll({
            top: target.offsetTop - 175,
            left: 0,
            behavior: 'smooth'
          })
        }
      },
      unselectAll() {
        this.dashboards.forEach(x => {
          this[x.showFlag] = false
        })
        this.saveSectionVisibility()
      },
      selectAll() {
        this.dashboards.forEach(x => {
          this[x.showFlag] = true
        })
        this.saveSectionVisibility()
      },
      shouldShowDashboardOption(dashboard) {
        return (dashboard.name !== 'By Original Auditor' || this.showLevel2Audit) && (!dashboard.featureFlag || (this.tenantSetup && this.tenantSetup[dashboard.featureFlag]))
      },
      chunkDashboards(totalChunks) {
        const dashboards = this.dashboards.filter(x => this.shouldShowDashboardOption(x))
        const totalItems = dashboards.length
        const baseChunkSize = Math.floor(totalItems / totalChunks)
        const remainder = totalItems % totalChunks
        const chunkedResult = []

        let currentIndex = 0
        for (let i = 0; i < totalChunks; i++) {
          const chunkSize = baseChunkSize + (i < remainder ? 1 : 0)
          chunkedResult.push(dashboards.slice(currentIndex, currentIndex + chunkSize))
          currentIndex += chunkSize
        }

        return chunkedResult
      },
      getShowFlag(showFlag) {
        return this[showFlag]
      },
      toggleShowFlag(showFlag) {
        this[showFlag] = !this[showFlag]
        this.saveSectionVisibility()
      },
      saveSectionVisibility() {
        // Build an object with all showFlag key-value pairs
        const sectionVisibility = {}
        this.dashboards.forEach(dashboard => {
          sectionVisibility[dashboard.showFlag] = this[dashboard.showFlag]
        })
        window.localStorage.setItem('ambulatorySectionVisibility', JSON.stringify(sectionVisibility))
      }
    },
    created: async function() {
      this.loading = true
      const ids = window.localStorage.getItem('reportIds')
      const title = window.localStorage.getItem('reportTitle')
      const codeTypes = window.localStorage.getItem('codeTypes')
      const codeTypesDesc = window.localStorage.getItem('codeTypesDesc')
      const isReportSection = window.localStorage.getItem('isReportSection')
      this.showLevel2Audit = window.localStorage.getItem('showLevel2Audit') === 'true'
      const sectionVisibility = window.localStorage.getItem('ambulatorySectionVisibility')

      // Section Visibility Format: {"showByCoder": true, "showByFacility": false, ...}
      if (sectionVisibility) {
        try {
          const parsedVisibility = JSON.parse(sectionVisibility)
          if (typeof parsedVisibility === 'object' && parsedVisibility !== null) {
            Object.keys(parsedVisibility).forEach(showFlag => {
              if (showFlag in this) {
                this[showFlag] = parsedVisibility[showFlag]
              }
            })
          }
        } catch (e) {
          console.warn('Failed to parse ambulatorySectionVisibility from localStorage:', e)
        }
      }

      const calcDashboards = this.dashboards

      if (isReportSection === 'false') {
        for (let i = 0; i <= this.dashboards.length - 1; i++) {
          if (this.dashboards[i].reportSectionOnly) {
            calcDashboards.splice(i, 1)
          }
        }
      }

      let arrCodeTypes = []
      if (codeTypes !== '') {
        arrCodeTypes = codeTypes.split(',')
        arrCodeTypes.forEach(a => {
          a = parseInt(a)
        })
      }

      this.ascTableIds = ids
      this.title = title
      this.codeTypes = arrCodeTypes
      this.codeTypesDesc = codeTypesDesc
      window.document.title = this.title ? `Outpatient Coding Audit Results for ${this.title}` : 'Outpatient Coding Audit Results'
      await this.GET_TENANT_SETUP()
      this.loading = false
    },
    computed: {
      ...mapState('user/', ['baseApiUrl']),
      ...mapState('managedLists/tenantSetup/', ['tenantSetup'])
    }
  }
</script>

<style scoped>
  >>> .tag-danger {
    padding: 2px 8px;
    border-radius: 4px;
    background-color: #fdecec;
    border: 1px solid #f3b6b6;
    color: #b42318;
    font-size: 12px;
    font-weight: 700;
  }

  >>> .tag-success {
    padding: 2px 8px;
    border-radius: 4px;
    background-color: #eef8f0;
    border: 1px solid #b9e2c2;
    color: #1f7a3a;
    font-size: 12px;
    font-weight: 700;
  }

  >>> .findings-cell {
    padding: 12px 16px;
    height: 60px;
    position: relative;
  }

  >>> .findings-label {
    font-size: 14px;
    color: #4f5661;
    display: block;
    margin-bottom: 6px;
  }

  >>> .findings-value {
    font-size: 16px;
    font-weight: 600;
    color: #1f2328;
    position: absolute;
    bottom: 12px;
    left: 16px;
  }

  >>> .findings-value-row {
    position: absolute;
    bottom: 12px;
    left: 16px;
    right: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  >>> .findings-value-text {
    font-size: 16px;
    font-weight: 600;
    color: #1f2328;
  }

  .toc {
    font-size: 14px;
    margin: 8px;
  }

  .toc a {
    color: #606266;
  }

  .toc a:hover {
    color: #337ab7;
    text-decoration: underline;
  }

  .report-header-row {
    box-shadow: 0px 4px 4px -6px rgba(0, 0, 0, 0.8) !important;
    position: sticky;
    top: 0;
    z-index: 1000;
    padding: 20px 0px 20px 0px;
    background: #fff;
    left: 0;
    right: 0;
    width: 100%;
  }

  >>> .strong {
    font-weight: bold;
  }

  >>> .emphasis {
    font-style: italic;
  }

  .separator {
    height: 15px;
    background-color: lightslategray;
  }

  .red-font {
    color: red;
  }

  >>> table {
    border-spacing: 0px;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 14px;
  }

  >>> td,
  >>> th {
    margin: 0px;
    padding: 10px 6px 10px 6px;
    vertical-align: top;
  }

  >>> thead {
    text-align: left !important;
    padding-top: 5px;
    /* background-color: #e1f5fe; */
  }

  >>> tr:nth-child(even) {
    background-color: #fafafa;
  }

  >>> .worksheet .demographics tr:nth-child(even) {
    background-color: unset;
  }

  >>> .worksheet .stripe tr:nth-child(even) {
    background-color: unset;
  }

  h1 {
    margin: 40px 0px 20px 0px;
    font-size: 2rem;
    border-bottom: 1px solid #b4bccc;
    padding-bottom: 5px;
  }

  h2 {
    margin: 50px 0px 20px 0px;
    font-size: 1.5rem;
    border-bottom: 1px solid #b4bccc;
    padding-bottom: 5px;
  }

  .options label {
    margin: 5px 5px 5px 5px;
  }

  @media screen and (min-width: 1200px) {
    .hidden-lg {
      display: none;
    }
  }

  @media screen and (max-width: 1200px) {
    .hidden-sm {
      display: none;
    }
  }

  .watermark {
    display: none;
  }

  @media print {
    .watermark {
      display: block;
      font-size: 8px;
      bottom: 0;
      position: fixed;
      right: 10px;
      font-style: italic;
    }

    .page-break {
      display: block;
      page-break-before: always;
    }

    .print-hidden {
      display: none;
    }
  }
</style>
